// src/features/lockers/types/index.ts
import { RaRecord, Identifier } from 'react-admin';

// ============================================
// ENUMS
// ============================================

export enum LockerStatus {
  Available = 'Available',
  Occupied = 'Occupied',
  Reserved = 'Reserved',
  Blocked = 'Blocked'
}

export enum LockerSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  ExtraLarge = 'extra_large'
}

// ============================================
// INTERFACES BASE
// ============================================

export interface LockerBase {
  id: Identifier;
  displayNumber: string;
  hardwareId: string;
  status: LockerStatus;
  statusReason?: string;
  isActive: boolean;
  hotelId: string;
  zoneId: string;
  lockerTypeId: string;
  lockerControllerId?: string;
  physicalPosition?: {
    row: number;
    column: number;
    section?: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// INTERFACES RELACIONADAS
// ============================================

export interface ZoneSummary {
  id: string;
  name: string;
  description?: string;
  floor?: number;
  section?: string;
}

export interface LockerTypeSummary {
  id: string;
  name: string;
  size: LockerSize;
  description?: string;
  capacity?: number;
  // No incluir icon aquí, se manejará diferente
}

export interface AssignmentSummary {
  id: string;
  guestName?: string;
  roomNumber?: string;
  tagId?: string;
  assignedAt: string;
  expiresAt?: string;
  type: 'guest' | 'employee' | 'temporary';
  assignmentId: string;
  displayReference: string;
  status: string;
}

export interface ControllerSummary {
  id: string;
  name: string;
  ipAddress: string;
  tcpPort: number;
  isOnline: boolean;
}

// ============================================
// INTERFACE PARA LISTA
// ============================================

export interface Locker extends LockerBase, RaRecord {
  zone: ZoneSummary;
  lockerType: LockerTypeSummary;
  assignment?: AssignmentSummary;
  controller?: ControllerSummary;
  lastMaintenanceAt?: string;
}

// ============================================
// INTERFACES PARA FORMULARIOS
// ============================================

export interface LockerFormData {
  displayNumber: string;
  hardwareId: string;
  status?: LockerStatus;
  hotelId: string;
  zoneId: string;
  lockerTypeId: string;
  lockerControllerId?: string;
  notes?: string;
  isActive?: boolean;
  physicalPosition?: {
    row: number;
    column: number;
    section?: string;
  };
}

export interface LockerBatchFormData {
  prefix: string;
  startNumber: number;
  endNumber: number;
  hotelId: string;
  zoneId: string;
  lockerTypeId: string;
  lockerControllerId?: string;
  section?: string;
  startRow?: number;
  startColumn?: number;
  notes?: string;
}

export interface CreateLockerRequest {
  displayNumber: string;
  hardwareId: string;
  hotelId: string;
  zoneId: string;
  lockerTypeId: string;
  lockerControllerId?: string;
  notes?: string;
  physicalPosition?: {
    row: number;
    column: number;
    section?: string;
  };
}

export interface UpdateLockerRequest {
  displayNumber?: string;
  status?: LockerStatus;
  statusReason?: string;
  zoneId?: string;
  lockerTypeId?: string;
  lockerControllerId?: string;
  notes?: string;
  isActive?: boolean;
  physicalPosition?: {
    row: number;
    column: number;
    section?: string;
  };
}

// ============================================
// FUNCIONES HELPER
// ============================================

/**
 * Obtiene el color para un estado de locker
 */
export const getLockerStatusColor = (status: LockerStatus): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status) {
    case LockerStatus.Available:
      return 'success';
    case LockerStatus.Occupied:
      return 'info';
    case LockerStatus.Blocked:
      return 'error';
    case LockerStatus.Reserved:
      return 'default';
    default:
      return 'default';
  }
};

/**
 * Obtiene la etiqueta traducida para un estado
 */
export const getLockerStatusLabel = (status: LockerStatus): string => {
  const labels: Record<LockerStatus, string> = {
    [LockerStatus.Available]: 'Disponible',
    [LockerStatus.Occupied]: 'Ocupada',
    [LockerStatus.Reserved]: 'Reservada',
    [LockerStatus.Blocked]: 'Bloqueada',
  };
  return labels[status] || status;
};

/**
 * Obtiene el icono para un tamaño de locker
 */
export const getLockerSizeIcon = (size: LockerSize): string => {
  const icons: Record<LockerSize, string> = {
    [LockerSize.Small]: '📦',
    [LockerSize.Medium]: '📦',
    [LockerSize.Large]: '📦',
    [LockerSize.ExtraLarge]: '📦',
  };
  return icons[size] || '📦';
};

/**
 * Valida si un número de locker es válido
 */
export const isValidLockerNumber = (number: string): boolean => {
  // Formato esperado: A-001, B-102, etc.
  const pattern = /^[A-Z]-\d{3}$/;
  return pattern.test(number) || /^\d{1,4}$/.test(number);
};

/**
 * Valida si un hardware ID es válido
 */
export const isValidHardwareId = (id: string): boolean => {
  // Hexadecimal de 8-16 caracteres
  const pattern = /^[A-F0-9]{8,16}$/i;
  return pattern.test(id);
};

/**
 * Prepara los datos del locker para la API
 */
export const prepareLockerForAPI = (data: LockerFormData): CreateLockerRequest => {
  return {
    displayNumber: data.displayNumber.trim(),
    hardwareId: data.hardwareId.trim().toUpperCase(),
    hotelId: data.hotelId,
    zoneId: data.zoneId,
    lockerTypeId: data.lockerTypeId,
    lockerControllerId: data.lockerControllerId,
    notes: data.notes?.trim(),
    physicalPosition: data.physicalPosition,
  };
};

/**
 * Prepara los datos para creación masiva de lockers
 */
export const prepareLockerBatchForAPI = (data: LockerBatchFormData): CreateLockerRequest[] => {
  const lockers: CreateLockerRequest[] = [];
  const { prefix, startNumber, endNumber, hotelId, zoneId, lockerTypeId, lockerControllerId, section, startRow, startColumn, notes } = data;
  
  for (let i = startNumber; i <= endNumber; i++) {
    const displayNumber = `${prefix}${i.toString().padStart(3, '0')}`;
    const row = startRow ? startRow + Math.floor((i - startNumber) / 10) : undefined;
    const column = startColumn ? startColumn + ((i - startNumber) % 10) : undefined;
    
    lockers.push({
      displayNumber,
      hardwareId: `HW${displayNumber}`,
      hotelId,
      zoneId,
      lockerTypeId,
      lockerControllerId,
      notes,
      physicalPosition: row && column ? { row, column, section } : undefined,
    });
  }
  
  return lockers;
};

/**
 * Determina si un locker está disponible para asignación
 */
export const isLockerAvailable = (locker: Locker): boolean => {
  return locker.status === LockerStatus.Available && 
         locker.isActive && 
         !locker.assignment;
};

/**
 * Formatea la información de posición física
 */
export const formatPhysicalPosition = (position?: { row: number; column: number; section?: string }): string => {
  if (!position) return '-';
  const section = position.section ? `${position.section}-` : '';
  return `${section}F${position.row}C${position.column}`;
};